// src/components/UserContractView/components/UserAssignmentDialog.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Avatar,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Zoom,
  Card,
  CardContent,
  Badge,
} from '@mui/material';

import {
  Close as CloseIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  DocumentIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Send as SendIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

import client from '../../API';
import { isValidEmail } from './contractUtils';
import { RECIPIENT_TYPE } from './contractConstants';

const UserAssignmentDialog = ({ open, template, onClose, onAssign }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dueDate, setDueDate] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Selected recipients
  const [recipients, setRecipients] = useState([]);
  
  // New recipient states
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  const debounceTimeout = useRef(null);

  const steps = ['Select Recipients', 'Add Details', 'Review & Send'];

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setDueDate('');
      setMessage('');
      setSearchQuery('');
      setSearchResults([]);
      setRecipients([]);
      setInviteName('');
      setInviteEmail('');
    }
  }, [open]);

  // Search function
  const handleSearchChange = async (value) => {
    setSearchQuery(value);
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    debounceTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      
      try {
        const data = await client.contracts.user.searchForAssignment({ q: value.trim() });
        setSearchResults(data.results || []);
        
        if (!data.found || (data.results && data.results.length === 0)) {
          if (data.search_type === 'email' || value.includes('@')) {
            setInviteEmail(value.trim());
            setInviteName('');
          }
        }
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleAddExisting = (user) => {
    if (recipients.some(r => r.type === RECIPIENT_TYPE.EXISTING && r.data.user_id === user.user_id)) {
      return;
    }

    setRecipients(prev => [
      ...prev,
      {
        type: RECIPIENT_TYPE.EXISTING,
        data: user,
      }
    ]);

    setSearchQuery('');
    setSearchResults([]);
  };

  const handleAddNewByEmail = () => {
    if (!isValidEmail(inviteEmail) || !inviteName.trim()) {
      return;
    }

    setRecipients(prev => [
      ...prev,
      { 
        type: RECIPIENT_TYPE.NEW_EMAIL, 
        data: { name: inviteName.trim(), email: inviteEmail.trim() } 
      }
    ]);

    setInviteName('');
    setInviteEmail('');
    setSearchQuery('');
  };

  const handleRemoveRecipient = (index) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (activeStep === 0 && recipients.length === 0) {
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!template || recipients.length === 0) {
      return;
    }

    try {
      setLoading(true);
      
      const existingUsers = recipients
        .filter(r => r.type === RECIPIENT_TYPE.EXISTING)
        .map(r => r.data.user_id);
      
      const emailInvites = recipients
        .filter(r => r.type === RECIPIENT_TYPE.NEW_EMAIL)
        .map(r => ({ name: r.data.name, email: r.data.email }));

      const assignmentData = {
        template_id: template.id,
        scope: 'specific_users',
        user_ids: existingUsers,
        email_invites: emailInvites,
        due_date: dueDate || null,
        assignment_message: message || null,
      };

      await onAssign(assignmentData);
      onClose();
    } catch (error) {
      console.error('Failed to assign template:', error);
      alert(error.response?.data?.error || 'Failed to assign template');
    } finally {
      setLoading(false);
    }
  };

  if (!template) return null;

  const canProceed = activeStep === 0 ? recipients.length > 0 : true;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              <SendIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Assign Contract
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {template.name}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            disabled={loading}
            sx={{ 
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      <DialogContent sx={{ pt: 3 }}>
        {/* STEP 1: Select Recipients */}
        {activeStep === 0 && (
          <Fade in timeout={300}>
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ bgcolor: 'primary.50', borderColor: 'primary.200' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <GroupIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Who should receive this contract?
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Search for existing users or invite new people via email
                  </Typography>
                </CardContent>
              </Card>

              {/* Search Box */}
              <Box>
                <TextField
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name, username, or email..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: isSearching ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    }
                  }}
                />
              </Box>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    Found {searchResults.length} {searchResults.length === 1 ? 'person' : 'people'}
                  </Typography>
                  <Stack spacing={1.5}>
                    {searchResults.map((result) => {
                      const isAdded = recipients.some(
                        r => r.type === RECIPIENT_TYPE.EXISTING && r.data.user_id === result.user_id
                      );

                      return (
                        <Zoom in key={result.user_id} timeout={200}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s',
                              borderColor: isAdded ? 'success.main' : 'divider',
                              bgcolor: isAdded ? 'success.50' : 'background.paper',
                              '&:hover': {
                                boxShadow: 2,
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={2}>
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                  isAdded ? (
                                    <CheckIcon 
                                      sx={{ 
                                        bgcolor: 'success.main', 
                                        color: 'white',
                                        borderRadius: '50%',
                                        fontSize: 16,
                                        p: 0.25
                                      }} 
                                    />
                                  ) : null
                                }
                              >
                                <Avatar 
                                  src={result.profile_pic}
                                  sx={{ width: 48, height: 48 }}
                                >
                                  {result.display_name?.[0] || result.email?.[0]}
                                </Avatar>
                              </Badge>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {result.display_name || result.email}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {result.email}
                                </Typography>
                              </Box>
                            </Box>
                            <Button
                              variant={isAdded ? 'outlined' : 'contained'}
                              size="small"
                              onClick={() => handleAddExisting(result)}
                              disabled={isAdded}
                              startIcon={isAdded ? <CheckIcon /> : <AddIcon />}
                            >
                              {isAdded ? 'Added' : 'Add'}
                            </Button>
                          </Paper>
                        </Zoom>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* No Results - Invite New */}
              {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                <Fade in timeout={300}>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3,
                      bgcolor: 'grey.50',
                      borderStyle: 'dashed',
                      borderWidth: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <EmailIcon color="primary" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        No match found - Invite via Email
                      </Typography>
                    </Box>
                    
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        placeholder="e.g., John Doe"
                        size="small"
                      />
                      
                      <TextField
                        fullWidth
                        label="Email Address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        error={inviteEmail && !isValidEmail(inviteEmail)}
                        helperText={inviteEmail && !isValidEmail(inviteEmail) ? 'Invalid email format' : ''}
                        placeholder="e.g., john@example.com"
                        size="small"
                      />

                      <Button
                        variant="contained"
                        onClick={handleAddNewByEmail}
                        fullWidth
                        disabled={!inviteName.trim() || !isValidEmail(inviteEmail)}
                        startIcon={<AddIcon />}
                      >
                        Add Recipient
                      </Button>
                    </Stack>
                  </Paper>
                </Fade>
              )}

              {/* Selected Recipients */}
              {recipients.length > 0 && (
                <Box>
                  <Box 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    mb={1.5}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Selected Recipients
                    </Typography>
                    <Chip 
                      label={`${recipients.length} ${recipients.length === 1 ? 'person' : 'people'}`}
                      size="small"
                      color="primary"
                    />
                  </Box>
                  <Stack spacing={1.5}>
                    {recipients.map((recipient, index) => (
                      <Zoom in key={index} timeout={200}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: 'primary.50',
                            borderColor: 'primary.200'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            {recipient.type === RECIPIENT_TYPE.EXISTING ? (
                              <>
                                <Avatar src={recipient.data.profile_pic} sx={{ width: 40, height: 40 }}>
                                  {recipient.data.display_name?.[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {recipient.data.display_name || recipient.data.email}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {recipient.data.email}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                                  <EmailIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {recipient.data.name}
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                      {recipient.data.email}
                                    </Typography>
                                    <Chip label="New Invite" size="small" color="secondary" sx={{ height: 18 }} />
                                  </Box>
                                </Box>
                              </>
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveRecipient(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      </Zoom>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Fade>
        )}

        {/* STEP 2: Add Details */}
        {activeStep === 1 && (
          <Fade in timeout={300}>
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ bgcolor: 'info.50', borderColor: 'info.200' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Optional: Add context and deadline
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Help recipients understand the urgency and context
                  </Typography>
                </CardContent>
              </Card>

              <TextField
                fullWidth
                type="datetime-local"
                label="Due Date (Optional)"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Set a deadline for when this contract should be signed"
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message (Optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message for the recipient(s)..."
                helperText="This message will be visible when recipients view the contract"
              />
            </Stack>
          </Fade>
        )}

        {/* STEP 3: Review */}
        {activeStep === 2 && (
          <Fade in timeout={300}>
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ bgcolor: 'success.50', borderColor: 'success.200' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Ready to send!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review the details before sending the contract
                  </Typography>
                </CardContent>
              </Card>

              {/* Contract Info */}
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Contract
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {template.name}
                  </Typography>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {template.description}
                    </Typography>
                  )}
                </Paper>
              </Box>

              {/* Recipients Summary */}
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Recipients ({recipients.length})
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Stack spacing={1}>
                    {recipients.map((recipient, index) => (
                      <Box key={index} display="flex" alignItems="center" gap={1.5}>
                        <Avatar 
                          src={recipient.type === RECIPIENT_TYPE.EXISTING ? recipient.data.profile_pic : null}
                          sx={{ width: 32, height: 32 }}
                        >
                          {recipient.type === RECIPIENT_TYPE.EXISTING 
                            ? recipient.data.display_name?.[0] 
                            : <EmailIcon fontSize="small" />
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {recipient.type === RECIPIENT_TYPE.EXISTING 
                              ? recipient.data.display_name || recipient.data.email
                              : recipient.data.name
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {recipient.data.email}
                            {recipient.type === RECIPIENT_TYPE.NEW_EMAIL && ' (New Invite)'}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>

              {/* Details */}
              {(dueDate || message) && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Additional Details
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Stack spacing={1.5}>
                      {dueDate && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Due Date
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {new Date(dueDate).toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      {message && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Message
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {message}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Fade>
        )}
      </DialogContent>
      
      <Divider />

      <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'grey.50' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button 
            onClick={onClose} 
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          
          <Box display="flex" gap={1}>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </Button>
            )}
            
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || recipients.length === 0}
                startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                sx={{ minWidth: 140 }}
              >
                {loading ? 'Sending...' : 'Send Contract'}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserAssignmentDialog;